import { useState, type CSSProperties } from "react";

import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { slotOf, type Person } from "@/lib/people";
import {
  noteImagePath,
  type ImageBox,
  type NoteImageMetadata,
} from "../note-image.ts";

type ReadyImage = Extract<NoteImageMetadata, { kind: "ready" }>;

/**
 * The subject of an open note: the captured image, at most 660 by 406, with
 * the author's pin and outline drawn over the noted element. The image is
 * read through the existing private HTTP path only for the open note. Images
 * without capture geometry show no overlay.
 */
export function NoteSubjectImage({
  author,
  image,
  noteId,
  siteId,
}: {
  readonly author: Person;
  readonly image: Exclude<NoteImageMetadata, { kind: "absent" }>;
  readonly noteId: string;
  readonly siteId: string;
}) {
  if (image.kind === "invalid")
    return (
      <div className="note-shot" data-state="failed" style={frameSize(16, 10)}>
        <div className="note-shot-frame">
          <p className="note-shot-status" role="status">
            Image details are unavailable. Refresh the notes to try again.
          </p>
        </div>
      </div>
    );
  return (
    <StoredImage
      author={author}
      image={image}
      key={siteId + ":" + noteId + ":" + image.id}
      path={noteImagePath(siteId, noteId)}
    />
  );
}

function StoredImage({
  author,
  image,
  path,
}: {
  readonly author: Person;
  readonly image: ReadyImage;
  readonly path: string;
}) {
  const [attempt, setAttempt] = useState(0);
  return (
    <ImageAttempt
      author={author}
      image={image}
      key={attempt}
      path={path + "?attempt=" + attempt}
      retry={() => setAttempt((current) => current + 1)}
    />
  );
}

/** Reserve the fitted box before the image loads, so nothing jumps. */
function frameSize(width: number, height: number): CSSProperties {
  return {
    aspectRatio: `${width} / ${height}`,
    width: `min(660px, 100%, calc(406px * ${width} / ${height}))`,
  };
}

function ImageAttempt({
  author,
  image,
  path,
  retry,
}: {
  readonly author: Person;
  readonly image: ReadyImage;
  readonly path: string;
  readonly retry: () => void;
}) {
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading");
  const description = image.reconstructed
    ? "Saved DOM reconstruction of the website when this note was created."
    : "Image attached when this note was created.";
  return (
    <Dialog>
      <div
        className="note-shot"
        data-state={state}
        style={frameSize(image.width, image.height)}
      >
        <DialogTrigger
          aria-label="View image full size"
          className="note-shot-frame"
          disabled={state !== "ready"}
        >
          {state !== "failed" && (
            <img
              alt="Attached visual context"
              data-ready={state === "ready"}
              decoding="async"
              height={image.height}
              onError={() => setState("failed")}
              onLoad={() => setState("ready")}
              referrerPolicy="no-referrer"
              src={path}
              width={image.width}
            />
          )}
        </DialogTrigger>
        {state === "ready" && image.targets !== null ? (
          <TargetOverlay author={author} targets={image.targets} />
        ) : null}
        {state === "ready" ? null : (
          <div className="note-shot-status" role="status">
            {state === "loading"
              ? "Loading image…"
              : "Image could not be loaded."}
            {state === "failed" && (
              <Button onClick={retry} size="sm" type="button" variant="outline">
                Retry image
              </Button>
            )}
          </div>
        )}
      </div>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Attached image</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <ExpandedImage height={image.height} path={path} width={image.width} />
      </DialogContent>
    </Dialog>
  );
}

const percent = (value: number): string => `${(value * 100).toFixed(3)}%`;

/**
 * The author's outline on every visible target and their pin on the first.
 * The outline sits on the element's edge, where the SDK bakes its own line
 * into the image, so the reader sees one ring in the author's colour. The pin
 * follows the SDK rule: the element's top right corner, 4px in and 14px up,
 * kept inside the image.
 */
function TargetOverlay({
  author,
  targets,
}: {
  readonly author: Person;
  readonly targets: readonly ImageBox[];
}) {
  const first = targets[0];
  if (first === undefined) return null;
  const colour =
    author.kind === "member" ? `var(--p${slotOf(author.id)})` : "var(--fg-2)";
  return (
    <div
      aria-hidden="true"
      className="note-shot-overlay"
      style={{ "--author": colour } as CSSProperties}
    >
      {targets.map((box, index) => (
        <span
          className="note-shot-outline"
          key={index}
          style={{
            left: percent(box.left),
            top: percent(box.top),
            width: percent(box.right - box.left),
            height: percent(box.bottom - box.top),
          }}
        />
      ))}
      <span
        className="note-shot-pin"
        style={{
          left: `clamp(0px, calc(${percent(first.right)} - 4px), calc(100% - 24px))`,
          top: `clamp(0px, calc(${percent(first.top)} - 14px), calc(100% - 24px))`,
        }}
      >
        <Avatar letters="one" person={author} />
      </span>
    </div>
  );
}

function ExpandedImage({
  path,
  width,
  height,
}: {
  readonly path: string;
  readonly width: number;
  readonly height: number;
}) {
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  return (
    <div className="flex min-h-32 items-center justify-center rounded-md bg-bg-sunken">
      {failed ? (
        <div className="flex flex-wrap items-center gap-3 p-4">
          <p role="status">Image could not be loaded.</p>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setAttempt((value) => value + 1);
              setFailed(false);
            }}
          >
            Retry image
          </Button>
        </div>
      ) : (
        <img
          key={attempt}
          src={path + "&view=" + attempt}
          alt="Saved visual context for this note"
          width={width}
          height={height}
          className="max-h-[65dvh] max-w-full object-contain"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}
